/**
 * @file React hook — useGeographicScopeDropdown.ts: form/state logic for contributor dashboard.
 */

import { useCallback, useEffect, useState } from "react";
import axiosClient from "../api/axiosClient";
import { FORM_STAGE_CODES } from "../constants/formStages";
import type { FormDataResponse, FormField } from "../interfaces/formInterfaces";
import { DropdownOption } from "../interfaces/dropdownInterfaces";
import {
  fetchDropdownOptionsById,
  type CmsDropdownOptionRow,
} from "../services/dropdownOptionsPublicService";
import { getPublicationFormData } from "../services/publicationService";

/** Exported const — PUBLICATION GEOGRAPHIC SCOPES DROPDOWN NAME. */
export const PUBLICATION_GEOGRAPHIC_SCOPES_DROPDOWN_NAME =
  "Publication Geographic Scopes";

const extractDropdownOptions = (payload: unknown): DropdownOption[] => {
  if (!payload) return [];
  const p = payload as Record<string, unknown>;
  if (Array.isArray(payload)) return normalizeOptionRows(payload);
  if (Array.isArray(p.options)) return normalizeOptionRows(p.options);
  if (Array.isArray((p.data as Record<string, unknown>)?.options)) {
    return normalizeOptionRows(
      (p.data as { options: Record<string, unknown>[] }).options
    );
  }
  if (Array.isArray(p.data)) return normalizeOptionRows(p.data);
  return [];
};

function normalizeOptionRows(rows: unknown[]): DropdownOption[] {
  return (rows as Record<string, unknown>[])
    .map((o, index) => {
      const idRaw = o.id != null ? Number(o.id) : index + 1;
      const value = String(o.value ?? o.id ?? "").trim();
      const label = String(o.label ?? o.value ?? o.id ?? "").trim();
      return {
        id: Number.isFinite(idRaw) ? idRaw : index + 1,
        label,
        value,
      };
    })
    .filter((o) => o.value && o.label);
}

function rowsToDropdownOptions(rows: CmsDropdownOptionRow[]): DropdownOption[] {
  return rows.map((o, index) => ({
    id: o.id ?? index + 1,
    label: o.label,
    value: o.value,
  }));
}

function normalizeDropdownCatalog(payload: unknown): Array<{
  id?: number;
  name?: string;
  options?: unknown[];
}> {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload as Array<{ id?: number; name?: string }>;
  const p = payload as Record<string, unknown>;
  if (Array.isArray(p.data)) {
    return p.data as Array<{ id?: number; name?: string }>;
  }
  return [];
}

function findGeographicScopesFormField(
  form: FormDataResponse
): FormField | undefined {
  const allFields: FormField[] = [
    ...(form.fields || []),
    ...(form.ungroupedFields || []),
    ...(form.subSections?.flatMap((s) => s.fields || []) || []),
  ];
  return allFields.find((f) => f.name === "geographicScopes");
}

function findGeographicScopesCatalogEntry(
  dropdowns: Array<{ id?: number; name?: string; options?: unknown[] }>
) {
  const target = PUBLICATION_GEOGRAPHIC_SCOPES_DROPDOWN_NAME.toLowerCase();
  return dropdowns.find((d) => {
    const name = String(d.name ?? "").trim().toLowerCase();
    return (
      name === target ||
      name === "geographic scope(s)" ||
      name === "geographic scopes" ||
      (name.includes("geographic") && name.includes("scope"))
    );
  });
}

/** Same source as Publication form `geographicScopes` field. */
export async function fetchPublicationGeographicScopeOptions(): Promise<
  DropdownOption[]
> {
  try {
    const formResult = await getPublicationFormData(
      FORM_STAGE_CODES.PUBLICATION_DETAILS
    );
    if (formResult.success && formResult.data) {
      const geoField = findGeographicScopesFormField(formResult.data);
      if (geoField) {
        const embedded =
          geoField.dropdown?.options || geoField.dropdownValues || [];
        if (embedded.length > 0) {
          return sortGeographicScopeOptions(normalizeOptionRows(embedded));
        }

        const dropdownId =
          geoField.dropdownId != null
            ? Number(geoField.dropdownId)
            : geoField.dropdown?.id != null
              ? Number(geoField.dropdown.id)
              : NaN;

        if (Number.isFinite(dropdownId) && dropdownId > 0) {
          const rows = await fetchDropdownOptionsById(dropdownId);
          if (rows.length > 0) {
            return sortGeographicScopeOptions(rowsToDropdownOptions(rows));
          }
        }
      }
    }
  } catch (err) {
    console.warn("Publication form geographicScopes options unavailable", err);
  }

  try {
    const res = await axiosClient.get("/dropdowns");
    const dropdowns = normalizeDropdownCatalog(res.data);
    const match = findGeographicScopesCatalogEntry(dropdowns);

    if (match) {
      const fromMatch = extractDropdownOptions(match);
      if (fromMatch.length > 0) {
        return sortGeographicScopeOptions(fromMatch);
      }

      if (match.id != null) {
        const rows = await fetchDropdownOptionsById(Number(match.id));
        if (rows.length > 0) {
          return sortGeographicScopeOptions(rowsToDropdownOptions(rows));
        }
      }
    }
  } catch (err) {
    console.error("Failed to load geographic scope dropdown options", err);
  }

  return sortGeographicScopeOptions([]);
}

/** Exported const — GLOBAL GEOGRAPHIC SCOPE OPTION. */
export const GLOBAL_GEOGRAPHIC_SCOPE_OPTION: DropdownOption = {
  id: 0,
  label: "Global",
  value: "global",
};

/** Always include "Global" first; remaining options alphabetical by label. */
export function sortGeographicScopeOptions(
  options: DropdownOption[]
): DropdownOption[] {
  const rest = options
    .filter((o) => String(o.value ?? "").trim().toLowerCase() !== "global")
    .sort((a, b) =>
      String(a.label ?? a.value ?? "").localeCompare(
        String(b.label ?? b.value ?? "")
      )
    );
  return [GLOBAL_GEOGRAPHIC_SCOPE_OPTION, ...rest];
}

/** Publication "Geographic Scope" dropdown (same CMS data as publication form). */
export const useGeographicScopeDropdown = () => {
  const [geographicScopeOptions, setGeographicScopeOptions] = useState<
    DropdownOption[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDropdown = useCallback(async () => {
    setLoading(true);
    try {
      const options = await fetchPublicationGeographicScopeOptions();
      setGeographicScopeOptions(options);
      setError(options.length ? null : "No geographic scope options found");
    } catch (err) {
      console.error("Failed to load geographic scope dropdown options", err);
      setGeographicScopeOptions(sortGeographicScopeOptions([]));
      setError("Failed to load geographic scope options");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadDropdown();
  }, [loadDropdown]);

  return {
    geographicScopeOptions,
    loading,
    error,
    refetch: loadDropdown,
  };
};
